import type {
  AthleteStatus,
  AthleteView,
  SupportedLocale,
  UpdateAthleteRequest,
} from '@stm/contracts';
import type { TranslationKey } from '@stm/i18n';

export const athleteStatusLabelKeys: Readonly<Record<AthleteStatus, TranslationKey>> = {
  active: 'athletes.status.active',
  archived: 'athletes.status.archived',
  withdrawn: 'athletes.status.withdrawn',
};

/** Hungarian writes the family name first; English and Romanian write the given name first. */
export function athleteDisplayName(
  athlete: Pick<AthleteView, 'givenName' | 'familyName'>,
  locale: SupportedLocale,
): string {
  return locale === 'hu'
    ? `${athlete.familyName} ${athlete.givenName}`
    : `${athlete.givenName} ${athlete.familyName}`;
}

/**
 * A birth date is a calendar day without a time zone, so it is formatted as UTC; formatting it in
 * the browser's zone could show the previous day.
 */
export function formatCalendarDate(isoDate: string, locale: SupportedLocale): string {
  const parsed = new Date(`${isoDate}T00:00:00.000Z`);
  if (Number.isNaN(parsed.getTime())) return isoDate;
  return new Intl.DateTimeFormat(locale, { timeZone: 'UTC', dateStyle: 'medium' }).format(parsed);
}

export function athleteListPath(
  base: string,
  options: { search?: string; cursor?: string | null },
): string {
  const query = new URLSearchParams();
  const search = options.search?.trim();
  if (search !== undefined && search !== '') query.set('q', search);
  if (options.cursor !== undefined && options.cursor !== null) query.set('cursor', options.cursor);
  const text = query.toString();
  return `${base}/athletes${text === '' ? '' : `?${text}`}`;
}

export interface AthleteForm {
  givenName: string;
  familyName: string;
  dateOfBirth: string;
  email: string;
  phone: string;
}

export function formFromAthlete(athlete: AthleteView): AthleteForm {
  return {
    givenName: athlete.givenName,
    familyName: athlete.familyName,
    dateOfBirth: athlete.dateOfBirth,
    email: athlete.email ?? '',
    phone: athlete.phone ?? '',
  };
}

/** Only what the person actually changed is sent, so a concurrent edit of another field survives. */
export function changesFromForm(
  original: AthleteView,
  form: AthleteForm,
): Omit<UpdateAthleteRequest, 'reason'> {
  const changes: Omit<UpdateAthleteRequest, 'reason'> = {};
  if (form.givenName.trim() !== original.givenName) changes.givenName = form.givenName;
  if (form.familyName.trim() !== original.familyName) changes.familyName = form.familyName;
  if (form.dateOfBirth !== original.dateOfBirth) changes.dateOfBirth = form.dateOfBirth;
  if (form.email.trim() !== (original.email ?? '')) changes.email = form.email;
  if (form.phone.trim() !== (original.phone ?? '')) changes.phone = form.phone;
  return changes;
}

export function hasChanges(changes: object): boolean {
  return Object.keys(changes).length > 0;
}
